import { css } from 'aphrodite';
import React, { useEffect, useRef, useState } from 'react';
import { useUserPreferences } from '~/src/entities/user';
import Overlay from '../Modal/Overlay';
import styles from './styles';
import SyncSingleMatterModalContent, {
  ModalVariant,
} from './SyncSingleMatterModalContent';
import { useSyncSingleMatterTask } from '~/src/hooks/useSyncSingleMatterTask';
import { useSyncSingleMatter } from '~/src/entities/clioMatters/hooks';

type UseSyncSingleMatterModalOptions = {
  variant?: ModalVariant;
  alwaysRequireConfirm?: boolean;
  hideCheckbox?: boolean;
};

export const useSyncSingleMatterModal = (
  analyticsEventName: string,
  onSynced?: (matterId?: string) => void,
  options: Parameters<typeof useSyncSingleMatterTask>[0] = {},
  modalOptions: UseSyncSingleMatterModalOptions = {},
) => {
  const [clioMatterId, setClioMatterId] = useState<string>('');

  const hide = () => setClioMatterId('');

  const successToastMessage = 'Clio Manage data was refreshed successfully';

  const {
    isLoading,
    isError,
    isSuccess,
    error,
    sync,
    setTaskId,
    setIsLoading,
    reset,
  } = useSyncSingleMatter(
    analyticsEventName,
    successToastMessage,
    clioMatterId,
    hide,
    onSynced,
    undefined,
    options,
  );

  const performSync = async (clio_matter_id: string) => {
    setIsLoading(true);
    const { task_id } = await sync({
      clio_matter_id,
      location: analyticsEventName,
    });
    setTaskId(task_id);
  };

  const { data: userPreferences } = useUserPreferences();

  // Check user preferences to determine if confirmation is required
  // alwaysRequireConfirm bypasses the user preference check
  const requiresConfirm =
    modalOptions.alwaysRequireConfirm ||
    !userPreferences ||
    userPreferences.warn_sync_overwrite;

  const shouldShow = !!clioMatterId;
  const isConfirmed = isLoading || isError || isSuccess;
  const syncTriggeredRef = useRef(false);

  // When sync() is called before user preferences have loaded,
  // requiresConfirm is initially true (because !userPreferences).
  // Once preferences load and reveal the user opted out, auto-trigger the sync.
  useEffect(() => {
    if (clioMatterId && !requiresConfirm && !syncTriggeredRef.current) {
      syncTriggeredRef.current = true;
      performSync(clioMatterId).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clioMatterId, requiresConfirm]);

  const modal = (
    <Overlay
      onCancel={hide}
      show={shouldShow}
      disableClickCatcher={isLoading}
      onAfterClose={reset}
    >
      <div className={css(styles.modalContent)}>
        <SyncSingleMatterModalContent
          onCancel={hide}
          onConfirm={() => performSync(clioMatterId)}
          requiresConfirm={requiresConfirm && !isConfirmed}
          isError={isError}
          error={error}
          variant={modalOptions.variant}
          hideCheckbox={modalOptions.hideCheckbox}
        />
      </div>
    </Overlay>
  );

  return [
    {
      sync: (clio_matter_id: string) => {
        syncTriggeredRef.current = false;
        setClioMatterId(clio_matter_id);
        if (!requiresConfirm) {
          syncTriggeredRef.current = true;
          performSync(clio_matter_id).catch(() => {});
        }
      },
    },
    modal,
  ] as const;
};
